import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import type { UerPriceListRatioRowQuery } from 'gql/graphql';

import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { UseFormReset, UseFormSetValue } from 'react-hook-form/dist/types/form';

import { priceListRatioRowGql } from '../createOrEditRatioQueries';

interface IUsePriceListRatioRow {
    reset: UseFormReset<any>;
    setValue: UseFormSetValue<any>;
    setInitialGroupValue: React.Dispatch<any>;
    ratioRowId: number | null;
}

const requestId = 'priceListRatioRow';

export const usePriceListRatioRow = (props: IUsePriceListRatioRow) => {
    const { reset, setValue, ratioRowId, setInitialGroupValue } = props;

    const priceListRatioRowVariables = useMemo(
        () => ({
            ratioRowId,
        }),
        [ratioRowId],
    );

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerPriceListRatioRowQuery>) => {
            if (depsVars?.payload?.uerPriceListRatioRow?.result) {
                const data = depsVars?.payload?.uerPriceListRatioRow?.data;
                const ratioGroup = {
                    sectionId: data?.groupId,
                    sectionName: data?.groupName,
                    data,
                };

                reset(data);

                setValue(
                    'appliesToUerItems',
                    data?.appliesTo?.map((item) => ({
                        id: item.appliesToId,
                        code: item.appliesToCode,
                    })),
                );
                setValue('appliesToUerIds', data?.appliesTo?.map((item) => item.appliesToId));
                setValue('ratioGroup', ratioGroup);
                setValue('ratioGroupId', data?.groupId);

                setInitialGroupValue(ratioGroup);
            }
        },
        [reset, setInitialGroupValue, setValue],
    );

    const [priceListRatioRowData, priceListRatioRowStatus, , , priceListRatioRowSetData] = useRequest({
        requestId,
        query: priceListRatioRowGql,
        variables: priceListRatioRowVariables,
        withEffect: !!ratioRowId,
        dependenciesUpdater: onSuccess,
    });

    const priceListRatioRowDataItem = useMemo(
        () => priceListRatioRowData?.uerPriceListRatioRow?.data,
        [priceListRatioRowData?.uerPriceListRatioRow?.data],
    );

    return {
        priceListRatioRowStatus,
        priceListRatioRowSetData,
        priceListRatioRowDataItem,
    };
};
